import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  createComponent,
  inject,
} from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  ProgressSnackbarComponent,
  ProgressSnackbarState,
} from '../components/progress-snackbar/progress-snackbar.component';

export interface AsyncNotificationMessages {
  loading: string;
  success: string;
  error?: string;
}

const STATE_TITLES: Record<ProgressSnackbarState, string> = {
  loading: 'Veuillez patienter',
  success: 'Succès',
  error: 'Erreur',
};

// However fast the request settles, the resolved state must stay readable for at least this long.
const MINIMUM_DISPLAY_DURATION = 3000;
const SUCCESS_DISPLAY_DURATION = 4000;
const ERROR_DISPLAY_DURATION = 6000;

/**
 * Wraps an async action (typically an HTTP request) with a persistent corner
 * notification: shown as soon as the action starts, kept on screen while it
 * is in progress, then switched to a success/error state once it settles.
 *
 * Deliberately NOT built on MatSnackBar: MatSnackBar only ever keeps one
 * snack bar open and silently dismisses the current one the instant anything
 * else in the app opens a new one (e.g. the global HTTP error interceptor),
 * which made this toast disappear early regardless of its own timers. Instead
 * the toast is created and appended to document.body directly, independent
 * of any other notification in the app.
 */
@Injectable({ providedIn: 'root' })
export class AsyncNotificationService {
  private appRef = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private ngZone = inject(NgZone);

  track<T>(
    source$: Observable<T>,
    messages: AsyncNotificationMessages
  ): Observable<T> {
    const componentRef = this.show('loading', STATE_TITLES.loading, messages.loading);

    return source$.pipe(
      tap({
        next: () => this.resolve(componentRef, 'success', messages.success, SUCCESS_DISPLAY_DURATION),
        error: () =>
          this.resolve(
            componentRef,
            'error',
            messages.error || 'Une erreur est survenue',
            ERROR_DISPLAY_DURATION
          ),
      })
    );
  }

  private show(
    state: ProgressSnackbarState,
    title: string,
    message: string
  ): ComponentRef<ProgressSnackbarComponent> {
    const componentRef = createComponent(ProgressSnackbarComponent, {
      environmentInjector: this.environmentInjector,
    });
    componentRef.instance.data = { state, title, message };
    this.appRef.attachView(componentRef.hostView);
    document.body.appendChild(componentRef.location.nativeElement);
    // Don't wait for the next zone-triggered tick: render the initial state immediately.
    componentRef.changeDetectorRef.detectChanges();
    return componentRef;
  }

  private resolve(
    componentRef: ComponentRef<ProgressSnackbarComponent>,
    state: ProgressSnackbarState,
    message: string,
    displayDuration: number
  ) {
    // Run explicitly inside the zone and re-fetch the dismiss delay through
    // Math.max so the resolved state can never be evicted before it's had
    // time to actually be read, regardless of how fast the request settled.
    this.ngZone.run(() => {
      componentRef.instance.data = { state, title: STATE_TITLES[state], message };
      // Force the resolved state to render synchronously instead of relying
      // on the next automatic zone tick, which is what wasn't happening.
      componentRef.changeDetectorRef.detectChanges();
      const dismissAfter = Math.max(displayDuration, MINIMUM_DISPLAY_DURATION);
      setTimeout(
        () =>
          this.ngZone.run(() => {
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
          }),
        dismissAfter
      );
    });
  }
}
